/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { notifyBeforeNavigate } from "../../viewModel/videoPlayerUi";
import { Log } from "../../model/logging";
import { canNavigateBackInHistory } from "../../model/utility";

const TAG: string = "NavigationService";

interface NavigationService {
  /**
   * Navigate forward to a URL. NavigationInterceptor handles all side-effects.
   *
   * Pass `replace` to swap the current history entry instead of pushing a new
   * one. Use this for auth→protected transitions (splash/sign-in → home/player)
   * so the browser back button cannot return to an unprotected route.
   *
   * Pass `state` to hand the destination screen data via `useLocation().state`
   * (same mechanism `ProtectedRoute` uses for `state.from`) — e.g. the
   * content/unifiedCollection seed passed from Guide to Content Detail.
   */
  navigateTo: (
    url: string,
    replace?: boolean,
    state?: Record<string, unknown>
  ) => void;
  /** Navigate back. Uses nav stack to determine previous destination. */
  navigateBack: () => void;
  /**
   * Navigate back when browser history actually has an entry behind the
   * current one, and *replace* the current entry with `fallbackUrl` when it
   * does not.
   *
   * `navigate(-1)` is a silent no-op at the first history entry — the route
   * simply does not change, stranding the user on a screen with no way off it.
   * Any screen that can be the session's first entry needs this rather than a
   * bare `navigateBack()`: routes reached with `replace` (splash → player,
   * select-profile → player) and any route opened directly by URL.
   */
  navigateBackOrTo: (fallbackUrl: string) => void;
}

/**
 * Canonical navigation hook for all programmatic route transitions.
 *
 * All navigation side-effects (NetworkGuard cancellation, Redux
 * setLocationUrl, setActiveScreen, nav stack push/pop) are handled
 * centrally by NavigationInterceptor via useBlocker.
 *
 * This hook is intentionally minimal — it only triggers the transition.
 * For stack-derived state (canGoBack, previousUrl), use the selectors
 * from `viewModel/screens` directly via `useUnitySelector` in the
 * component that needs them — avoids unnecessary re-renders in
 * components that only call navigateTo/navigateBack.
 *
 * Usage:
 *   const { navigateTo, navigateBack } = useNavigationService();
 *   navigateTo(LocationUrls.GUIDE);
 */
export function useNavigationService(): NavigationService {
  const navigate = useNavigate();

  const navigateTo = useCallback(
    (url: string, replace?: boolean, state?: Record<string, unknown>): void => {
      // Synchronously give the PiP coordinator a chance to convert a Guide PiG
      // session into browser PiP while still inside this navigation gesture.
      notifyBeforeNavigate(url);
      navigate(url, { replace: replace === true, state });
    },
    [navigate]
  );

  const navigateBack = useCallback((): void => {
    notifyBeforeNavigate(null);
    navigate(-1);
  }, [navigate]);

  const navigateBackOrTo = useCallback(
    (fallbackUrl: string): void => {
      if (canNavigateBackInHistory()) {
        navigateBack();
        return;
      }
      Log.d(
        TAG,
        "navigateBackOrTo() no history entry behind current — replacing with " +
          fallbackUrl
      );
      // Replace, never push. This branch only runs at the app's first history
      // entry, so a push would stack the fallback *on top of* the screen the
      // user just asked to leave: the browser Back button would then walk them
      // straight back into it, and a second press would leave the app. Replacing
      // drops the departed screen, so Back exits once — which is the truth, as
      // nothing preceded the app in this tab.
      navigateTo(fallbackUrl, true);
    },
    [navigateBack, navigateTo]
  );

  return { navigateTo, navigateBack, navigateBackOrTo };
}
